import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logSecurityEvent } from '@/utils/securityLogger';
import { supabase } from '@/integrations/supabase/client';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isReloading: boolean;
}

/**
 * Error boundary that catches JavaScript chunk load errors (common on iOS after app updates)
 * and automatically reloads the page to fetch fresh chunks.
 */
class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isReloading: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is a chunk load error
    const isChunkError = 
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Importing a module script failed') ||
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('error loading dynamically imported module');
    
    if (isChunkError) {
      return { hasError: true, isReloading: true };
    }
    
    return { hasError: true, isReloading: false };
  }

  async componentDidCatch(error: Error, errorInfo: ErrorInfo): Promise<void> {
    console.error('[ChunkErrorBoundary] Caught error:', error.message);
    console.error('[ChunkErrorBoundary] Error info:', errorInfo);

    // Check if this is a chunk load error
    const isChunkError = 
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Importing a module script failed') ||
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('error loading dynamically imported module');

    // Log error to database for admin visibility
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      logSecurityEvent(
        isChunkError ? 'client_chunk_load_error' : 'client_error_boundary',
        user?.id || null,
        error.message || 'Unknown error',
        {
          error_name: error.name,
          component_stack: errorInfo.componentStack?.slice(0, 500) || null,
          page_url: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
          is_chunk_error: isChunkError,
          app_version: import.meta.env.VITE_APP_VERSION ?? null,
        }
      );
    } catch (logError) {
      console.error('[ChunkErrorBoundary] Failed to log error:', logError);
    }

    if (isChunkError) {
      console.log('[ChunkErrorBoundary] Chunk error detected, forcing hard navigation...');
      
      // Clear all caches first (including service worker caches)
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
        });
      }
      
      // Hard navigate with cache-bust query param so the browser/SW fetches
      // a fresh index.html instead of serving a stale cached version with
      // old chunk hashes. Using replace() avoids adding a broken history entry.
      setTimeout(() => {
        window.location.replace(
          window.location.origin + window.location.pathname + '?_bust=' + Date.now()
        );
      }, 100);
    }
  }

  render() {
    if (this.state.isReloading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center space-y-4 px-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
            <p className="text-muted-foreground text-sm">Updating app...</p>
          </div>
        </div>
      );
    }

    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center space-y-4 px-6">
            <p className="text-foreground font-medium">Something went wrong</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm"
            >
              Reload App
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
